import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AppShell } from "@/components/app-shell";
import { requireRole } from "@/lib/session";
import {
  getDueMasteryChecks,
  submitMasteryCheck,
  type DueMasteryCheck,
} from "@/lib/retention-checks.functions";
import { RefreshCw, CheckCircle2, Circle, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/learner/retention")({
  ssr: false,
  beforeLoad: () => requireRole("learner"),
  head: () => ({
    meta: [
      { title: "Quick Mastery Check — MAKAI" },
      {
        name: "description",
        content:
          "Short spaced-repetition checks to keep what you've learned strong — 3, 7, and 21 days after mastery.",
      },
      { property: "og:title", content: "Quick Mastery Check — MAKAI" },
      {
        property: "og:description",
        content:
          "Short, spaced checks that keep your learning strong after you first master a skill.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: RetentionPage,
});

function RetentionPage() {
  const { data, isLoading } = useQuery({
    queryKey: ["mastery-checks", "due"],
    queryFn: () => getDueMasteryChecks(),
  });

  const due = (data ?? []) as DueMasteryCheck[];

  return (
    <AppShell
      role="learner"
      title="Quick check"
      subtitle="Short spaced checks to keep your learning strong."
    >
      {isLoading ? (
        <div className="text-sm text-muted-foreground">Checking your schedule…</div>
      ) : due.length === 0 ? (
        <div className="rounded-2xl border border-border bg-card p-6 text-center shadow-soft">
          <Sparkles className="mx-auto h-6 w-6 text-primary" />
          <p className="mt-2 text-sm text-muted-foreground">
            Nothing due right now. New quick checks appear here 3, 7, and 21 days after you reach mastery.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {due.map((c) => (
            <CheckCard key={c.id} check={c} />
          ))}
        </div>
      )}
    </AppShell>
  );
}

function CheckCard({ check }: { check: DueMasteryCheck }) {
  const qc = useQueryClient();
  const total = 3;
  const [correct, setCorrect] = useState(0);
  const [done, setDone] = useState<null | { result: string }>(null);

  const submit = useMutation({
    mutationFn: () =>
      submitMasteryCheck({
        data: { checkId: check.id, correctCount: correct, totalCount: total },
      }),
    onSuccess: (res) => {
      setDone({ result: res.result });
      qc.invalidateQueries({ queryKey: ["mastery-checks", "due"] });
    },
  });

  if (done) {
    return (
      <div className="rounded-2xl border border-border bg-card p-4 shadow-soft">
        <div className="flex items-center gap-3">
          <CheckCircle2 className="h-5 w-5 text-emerald-500" />
          <div>
            <div className="font-medium">
              {check.node_label ?? check.node_key}
            </div>
            <div className="text-xs text-muted-foreground">
              Recorded as <span className="font-medium">{done.result}</span>.
              MAKAI will space the next check accordingly.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-border bg-card p-4 shadow-soft">
      <div className="flex items-start gap-3">
        <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary">
          <RefreshCw className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate font-medium">
            {check.node_label ?? check.node_key}
          </div>
          <div className="text-xs text-muted-foreground">
            Day-{check.interval_days} check
            {check.subject ? ` · ${check.subject}` : ""}
          </div>
        </div>
      </div>
      <div className="mt-3 text-xs text-muted-foreground">
        Tap how many of the {total} quick questions you got right:
      </div>
      <div className="mt-2 flex items-center gap-2">
        {Array.from({ length: total + 1 }).map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => setCorrect(i)}
            className={cn(
              "grid h-9 w-9 place-items-center rounded-full border text-sm",
              correct === i
                ? "border-primary bg-primary text-primary-foreground"
                : "border-border bg-background",
            )}
          >
            {correct === i ? <CheckCircle2 className="h-4 w-4" /> : <span>{i}</span>}
          </button>
        ))}
        <div className="ml-auto text-xs text-muted-foreground">/ {total}</div>
      </div>
      <button
        type="button"
        onClick={() => submit.mutate()}
        disabled={submit.isPending}
        className="mt-3 inline-flex items-center gap-1 rounded-lg bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground disabled:opacity-60"
      >
        {submit.isPending ? "Saving…" : "Submit check"}
      </button>
    </div>
  );
}

// avoid lint warning for unused import
void Circle;
